import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * CadenceRichSchemas
 * ------------------
 * Site-wide structured-data injector. Mounted ONCE in App.js so the
 * schemas live on every public route — that's what Google Knowledge
 * Panel logic + Rich Results needs.
 *
 * Three payloads, all glued together via `@id` so Google sees them
 * as one coherent entity graph:
 *
 *   1) Organization (MedicalBusiness)  — already exists in index.html
 *      static HTML; we DON'T duplicate it here.
 *   2) FAQPage                          — eligible for the
 *      expandable Q&A rich result. Conservative question set —
 *      excludes HIPAA / legal / pricing-comparison / clinical-advice
 *      questions that could surface unsafe snippets.
 *   3) MedicalBusiness `hasOfferCatalog` (services + memberships) —
 *      lets Google match CADENCE to "second opinion near me" /
 *      "medical advocate" queries with a structured price list.
 *   4) Person schemas for the two founders — keeps the founder
 *      entities linked to internal credentials anchors so the
 *      knowledge panel pulls the founder names + credentials
 *      directly from the on-site Founders page (no LinkedIn /
 *      external links needed).
 *
 * Founder spec 2026-02-08: routing Person.sameAs to in-site
 * credentials tile anchors instead of external LinkedIn URLs
 * ("I dont have linkedin URLs handy, route it to our credentials
 * tile so it acts as a linkable extension").
 */

const SITE = 'https://www.cadencemedicalconsulting.com';
const ORG_ID = `${SITE}/#organization`;

// ---------------------------------------------------------------
// 1) FAQPage — safe, search-friendly Q&A set.
// Excluded by design: HIPAA mechanics, legal liability, refunds,
// clinical/diagnostic advice, prescription details, anything that
// could be misread as medical guidance in a search snippet.
// ---------------------------------------------------------------
const FAQ_ENTRIES = [
  {
    q: 'What is CADENCE Medical Consulting?',
    a: 'CADENCE Medical Consulting is a veteran-owned, woman-led patient advocacy and second-opinion medical consulting firm. We provide independent medical record review, complex-diagnosis consultation, and clinical advocacy for patients who feel dismissed or overlooked by the standard system.',
  },
  {
    q: 'How does a second medical opinion work?',
    a: 'You share your medical records and questions through our secure intake. A board-certified nurse practitioner reviews your complete history — labs, imaging, prior notes, and the timeline of your symptoms — and then sits with you to translate it into a clear plan you can take into your next appointment.',
  },
  {
    q: 'Do you replace my doctor?',
    a: 'No. CADENCE works alongside your existing healthcare team. We do not prescribe medications, perform procedures, or take over your care. Our role is to clarify, advocate, and give you the language and confidence to lead the conversation with your treating physicians.',
  },
  {
    q: 'Who is CADENCE for?',
    a: 'CADENCE is for patients with complex or chronic conditions, anyone who feels unheard by their current providers, families navigating a confusing diagnosis, and people who simply want a medical professional in their corner before the next appointment.',
  },
  {
    q: 'What services do you offer?',
    a: 'Three core consultation packages — Diagnostic Bundle Review, Advocacy Launch Session, and Deep Dive Consultation — plus three membership tiers (Navigator, Advocate, Concierge) for patients managing ongoing complex care. A la carte services are also available for specific tasks like appointment-prep scripts or record summaries.',
  },
  {
    q: 'Where is CADENCE based?',
    a: 'CADENCE Medical Consulting, LLC is a Delaware limited liability company. We serve patients across the United States via secure telehealth consultation, with prescriptive authority licensure in Maryland and Delaware.',
  },
  {
    q: 'Is CADENCE veteran-owned?',
    a: 'Yes. CADENCE was founded by two U.S. military veterans with over 33 combined years of distinguished medical service — one a DNP-prepared nurse practitioner with 19+ years of military medicine, the other a 14-year Army Respiratory Technician (68V) with dual master\'s degrees in healthcare administration and disaster preparedness.',
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${SITE}/faq#faqpage`,
  mainEntity: FAQ_ENTRIES.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

// ---------------------------------------------------------------
// 2) OfferCatalog — service tiers + memberships pulled from the
// /pricing page. Pricing-comparison questions are kept OUT of the
// FAQ schema; structured Offer items here are the canonical way
// Google ingests pricing without the snippet-misread risk.
// ---------------------------------------------------------------
const CONSULTATION_OFFERS = [
  {
    name: 'Diagnostic Bundle Review',
    description: 'Comprehensive review of your diagnostic materials and lab results with expert analysis, written summary, and clarification of findings.',
    price: '495',
    url: `${SITE}/pricing#diagnostic-review`,
  },
  {
    name: 'Advocacy Launch Session',
    description: 'A focused 30-minute consultation that translates your complex history into a customized doctor-conversation script, a prioritized testing roadmap, and the confidence to lead your next appointment.',
    price: '1197',
    url: `${SITE}/pricing#advocacy-launch`,
  },
  {
    name: 'Deep Dive Consultation',
    description: 'A comprehensive 2-hour consultation with thorough investigation, advocacy strategy, and personalized care navigation. For complex cases requiring deep analysis.',
    price: '1797',
    url: `${SITE}/pricing#deep-dive`,
  },
];

const MEMBERSHIP_OFFERS = [
  {
    name: 'Navigator Membership',
    description: 'Monthly safety-net membership for graduates of a diagnostic package or anyone who wants periodic expert guidance.',
    price: '179',
    billing: 'P1M',
    url: `${SITE}/membership#navigator`,
  },
  {
    name: 'Advocate Membership',
    description: 'Monthly membership for patients actively managing a chronic or complex condition (PCOS, Long COVID, autoimmune, thyroid) who need consistent monthly strategy and support.',
    price: '399',
    billing: 'P1M',
    url: `${SITE}/membership#advocate`,
  },
  {
    name: 'Concierge Membership',
    description: 'High-touch monthly membership for individuals or families requiring comprehensive, hands-on navigation of complex care.',
    price: '799',
    billing: 'P1M',
    url: `${SITE}/membership#concierge`,
  },
];

const buildOfferCatalogJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'MedicalBusiness',
  '@id': ORG_ID,
  name: 'CADENCE Medical Consulting, LLC',
  url: SITE,
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'CADENCE Medical Consulting Services',
    itemListElement: [
      {
        '@type': 'OfferCatalog',
        name: 'Consultation Packages',
        itemListElement: CONSULTATION_OFFERS.map((o, i) => ({
          '@type': 'Offer',
          position: i + 1,
          name: o.name,
          description: o.description,
          price: o.price,
          priceCurrency: 'USD',
          url: o.url,
          availability: 'https://schema.org/InStock',
          category: 'Medical Consultation',
        })),
      },
      {
        '@type': 'OfferCatalog',
        name: 'Monthly Memberships',
        itemListElement: MEMBERSHIP_OFFERS.map((o, i) => ({
          '@type': 'Offer',
          position: i + 1,
          name: o.name,
          description: o.description,
          price: o.price,
          priceCurrency: 'USD',
          url: o.url,
          availability: 'https://schema.org/InStock',
          category: 'Medical Membership',
          eligibleDuration: { '@type': 'QuantitativeValue', value: 1, unitCode: 'MON' },
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: o.price,
            priceCurrency: 'USD',
            billingDuration: o.billing,
            unitCode: 'MON',
          },
        })),
      },
    ],
  },
});

// ---------------------------------------------------------------
// 3) Person schemas — knowledge-panel founder routing.
// `sameAs` deliberately points BACK into our own /founders page
// anchors. No external LinkedIn / social URLs were provided —
// the in-site credentials tile is the canonical extension of
// each founder's identity. Adding a Person ↔ Organization
// employee link via @id reuse builds the Knowledge-Graph triangle.
// ---------------------------------------------------------------
const FOUNDER_PEOPLE = [
  {
    id: `${SITE}/#lakia-stewart`,
    slug: 'lakia-stewart',
    name: 'Lakia S. Stewart',
    suffix: 'DNP, FNP-C, WHNP-BC',
    jobTitle: 'Chief Medical Officer & Co-Founder',
    description: 'DNP-prepared nurse practitioner with 19+ years of distinguished U.S. military medical service. Board-certified Family Nurse Practitioner and Women\'s Health Nurse Practitioner with full prescriptive authority in Maryland and Delaware.',
    image: `${SITE}/lakia-headshot.png`,
    credentials: [
      { type: 'degree', name: 'Doctor of Nursing Practice (DNP)' },
      { type: 'certification', name: 'Family Nurse Practitioner (FNP-C)' },
      { type: 'certification', name: 'Women\'s Health Nurse Practitioner (WHNP-BC)' },
      { type: 'license', name: 'NP Licensure with Full Prescriptive Authority (MD, DE)' },
      { type: 'license', name: 'RN Multi-State Licensure (FL, TX)' },
    ],
  },
  {
    id: `${SITE}/#andera-peeples`,
    slug: 'andera-peeples',
    name: 'Andera N. Peeples',
    suffix: 'MSDP, MSML-HA',
    jobTitle: 'Chief Executive Officer & Co-Founder',
    description: 'Healthcare systems architect with 14 years of distinguished service as a U.S. Army Respiratory Technician (68V). Dual master\'s degrees in Disaster Preparedness and Management & Leadership in Healthcare Administration.',
    image: `${SITE}/founder-leadership.png`,
    credentials: [
      { type: 'degree', name: 'Master of Science in Disaster Preparedness (MSDP)' },
      { type: 'degree', name: 'Master of Science in Management & Leadership — Healthcare Administration (MSML-HA)' },
    ],
  },
];

const founderPeopleJsonLd = FOUNDER_PEOPLE.map((p) => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': p.id,
  name: p.name,
  honorificSuffix: p.suffix,
  jobTitle: p.jobTitle,
  description: p.description,
  image: p.image,
  url: `${SITE}/founders#${p.slug}`,
  // sameAs intentionally routes back into the in-site credentials
  // tile (founder spec — no external LinkedIn URLs were provided).
  sameAs: [
    `${SITE}/founders#${p.slug}`,
    `${SITE}/about-us`,
  ],
  worksFor: { '@id': ORG_ID },
  affiliation: { '@id': ORG_ID },
  nationality: { '@type': 'Country', name: 'United States' },
  hasCredential: p.credentials.map((c) => ({
    '@type': 'EducationalOccupationalCredential',
    credentialCategory: c.type,
    name: c.name,
  })),
}));

export default function CadenceRichSchemas() {
  const offerCatalog = buildOfferCatalogJsonLd();

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(offerCatalog)}</script>
      {founderPeopleJsonLd.map((person) => (
        <script
          key={person['@id']}
          type="application/ld+json"
        >
          {JSON.stringify(person)}
        </script>
      ))}
    </Helmet>
  );
}
